import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Path, UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import useApiRequest from '../../../../../lib/hooks/useRequest';
import { formatDate } from '../../../../../lib/hooks/utils';
import { SelfCheckoutCodeResponseDto, SelfCheckoutCodeType } from '../../../../../lib/interfaces/organisation';
import { Checkbox } from '../../../../../ui/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../../ui/components/ui/form';
import { Input } from '../../../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../ui/components/ui/select';
import { PackageDisplayConfig } from '../../../../register/checkout/step2/config/package-mapper';
import { BILLING_FREQUENCY_OPTIONS, BillingFrequency } from '../../../../register/checkout/step2/config/packages-display.config';
import { GOLD_DISCOUNT_HINT } from '../../../../register/checkout/step2/config/self-checkout-codes.config';
import { PackageCategory } from '../../../../register/checkout/step2/enums/package-category.enum';
import { CapacityMetaResponseDto } from '../../../../register/checkout/step2/types/response';
import { CheckoutPaymentDataSchemaType } from '../data-schemas/payment-data-schema';
import { InvoiceDataFormItems } from './components/invoice-data-form-items';
import { StartDateSelector } from './components/start-date-selector';

interface PaymentFormItemsProps<T extends CheckoutPaymentDataSchemaType> {
  form: UseFormReturn<T>;
  isGermany?: boolean;
  capacities: CapacityMetaResponseDto;
  isValidating: boolean;
  ibanValid: boolean | undefined;
  bicValid: boolean | undefined;
  selectedBillingFrequency: BillingFrequency;
  setSelectedBillingFrequency: (frequency: BillingFrequency) => void;
  packages: Record<PackageCategory, PackageDisplayConfig>;
  selectedTier: PackageCategory;
  setSelectedTier: (tier: PackageCategory) => void;
  /** Meldet den aktuell eingelösten Code nach oben, damit die Paket-Karten darauf reagieren können. */
  setActiveCodeType: (codeType: SelfCheckoutCodeType | undefined) => void;
}

const PaymentFormItems = <T extends CheckoutPaymentDataSchemaType>({
  form,
  isGermany = true,
  capacities,
  isValidating,
  ibanValid,
  bicValid,
  selectedBillingFrequency,
  setSelectedBillingFrequency,
  packages,
  selectedTier,
  setSelectedTier,
  setActiveCodeType
}: PaymentFormItemsProps<T>) => {
  const { apiRequest } = useApiRequest();

  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyCodeResult = (codeValid: boolean | undefined, codeType: SelfCheckoutCodeType | undefined) => {
    form.setValue('codeValid' as Path<T>, codeValid as any);
    form.setValue('codeType' as Path<T>, codeType as any);
    setActiveCodeType(codeValid === true ? codeType : undefined);
  };

  const checkCode = async () => {
    setIsValidatingCode(true);
    const response = await apiRequest<SelfCheckoutCodeResponseDto>('self-checkout/code', 'POST', {
      body: {
        code: form.getValues('code' as Path<T>)
      }
    });

    if (!response.error && response.data) {
      applyCodeResult(response.data.valid, response.data.type);
    } else {
      applyCodeResult(false, undefined);
    }
    setIsValidatingCode(false);
  };

  const debouncedCheckCode = (codeValue: string) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Reset code validation state when code is empty
    if (!codeValue || codeValue.trim() === '') {
      applyCodeResult(undefined, undefined);
      setIsValidatingCode(false);
      return;
    }

    // Set new timer for 1.5 seconds
    debounceTimerRef.current = setTimeout(() => {
      checkCode();
    }, 1500);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // LITE-Code: automatisch das LITE-Paket auswählen bzw. zurücksetzen, wenn der Code entfernt wird
  const codeTypeWatched = form.watch('codeType' as Path<T>);
  const codeValidWatched = form.watch('codeValid' as Path<T>);
  const isLiteActive = codeValidWatched === true && codeTypeWatched === SelfCheckoutCodeType.LITE;

  useEffect(() => {
    if (isLiteActive) {
      if (selectedTier !== PackageCategory.LITE) setSelectedTier(PackageCategory.LITE);
    } else if (selectedTier === PackageCategory.LITE) {
      setSelectedTier(PackageCategory.BASIC);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiteActive]);

  // Sichtbare Tiers im Paket-Select: LITE versteckt die regulären Pakete, sonst Standard
  const visibleTiers = isLiteActive ? [PackageCategory.LITE] : [PackageCategory.BASIC, PackageCategory.PRO];

  // useInvoice: Auslandskunden zahlen immer auf Rechnung, DE-Kunden nur bei gültigem RECHNUNG-Code
  const isRechnungCodeActive = codeValidWatched === true && codeTypeWatched === SelfCheckoutCodeType.RECHNUNG;
  const useInvoice = !isGermany || isRechnungCodeActive;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-3 flex-col">
        <InvoiceDataFormItems form={form} isValidating={isValidating} ibanValid={ibanValid} bicValid={bicValid} useInvoice={useInvoice} />
      </div>
      <hr />
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-2xl font-semibold">Flatrate</h3>
          <p className="text-gray-400 text-xs">Wähle Deinen Plan und das Startdatum aus.</p>
        </div>
        <div className="w-full flex flex-col gap-3">
          {/* Package Category Select (shows current price) */}
          <div className="w-full">
            <FormItem className="flex flex-col bg-white rounded-lg">
              <FormControl>
                <Select onValueChange={(value) => setSelectedTier(value as PackageCategory)} value={selectedTier}>
                  <SelectTrigger className={!selectedTier ? 'text-muted-foreground' : ''}>
                    <SelectValue placeholder="Paket auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleTiers.map((category) => {
                      const price = packages[category]?.[selectedBillingFrequency]?.price;
                      const label = `${packages[category]?.[selectedBillingFrequency]?.packageName} (${price ?? ''}€/Monat)`;
                      return (
                        <SelectItem key={category} value={category}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          </div>
          <StartDateSelector
            form={form}
            capacities={capacities}
            currentCapacityValue={packages[selectedTier]?.[selectedBillingFrequency]?.capacity}
          />
          {/* Billing Frequency Select */}
          <div className="w-full">
            <FormItem className="flex flex-col bg-white rounded-lg">
              <FormControl>
                <Select onValueChange={(value) => setSelectedBillingFrequency(value as BillingFrequency)} value={selectedBillingFrequency}>
                  <SelectTrigger className={!selectedBillingFrequency ? 'text-muted-foreground' : ''}>
                    <SelectValue placeholder="Abrechnungszeitraum auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label} ({packages[selectedTier][option.value].additionalConditions})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          </div>
        </div>
      </div>
      <hr />
      <div className="w-full relative">
        <FormField
          control={form.control}
          name={'code' as Path<T>}
          render={({ field }) => {
            const codeValue = field.value as string;
            const codeValidState = form.watch('codeValid' as Path<T>);

            return (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Code"
                    value={codeValue}
                    className=""
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedCheckCode(e.target.value);
                    }}
                  />
                </FormControl>
                <div
                  className="absolute bottom-0 top-0 right-2 m-0 flex items-center justify-center"
                  style={{
                    marginTop: '0'
                  }}>
                  {isValidatingCode && <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />}
                  {!isValidatingCode && codeValue && codeValue.trim() !== '' && codeValidState === true && (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )}
                  {!isValidatingCode && codeValue && codeValue.trim() !== '' && codeValidState === false && (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
      {/* START50-Hinweis: 50% Rabatt auf den ersten Monat */}
      {form.watch('code' as Path<T>) &&
        form.getValues('code' as Path<T>) !== '' &&
        form.watch('codeValid' as Path<T>) === true &&
        form.watch('codeType' as Path<T>) === SelfCheckoutCodeType.START50 && (
          <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
            <div className="text-sm text-green-700">
              <span className="font-medium">-50 % Rabatt auf den ersten Monat der Zusammenarbeit</span>
            </div>
          </div>
        )}
      {/* GOLD-Hinweis: 1.000€ Rabatt auf den ersten Monat */}
      {codeValidWatched === true && codeTypeWatched === SelfCheckoutCodeType.GOLD && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
          <div className="text-sm text-green-700">
            <span className="font-medium">{GOLD_DISCOUNT_HINT}</span>
          </div>
        </div>
      )}
      {/* LITE-Hinweis: Lite-Flatrate freigeschaltet */}
      {isLiteActive && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
          <div className="text-sm text-green-700">
            <span className="font-medium">Lite Flatrate</span>
          </div>
        </div>
      )}
      {/* RECHNUNG-Hinweis: Auf Rechnung freigeschaltet */}
      {isRechnungCodeActive && (
        <div className="flex items-start gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
          <div className="text-sm text-green-700">
            <span className="font-medium">Zahlung auf Rechnung freigeschaltet</span>
          </div>
        </div>
      )}
      <div className="flex gap-6 flex-col">
        <div className="flex gap-3 flex-col">
          <FormField
            control={form.control}
            name={'termsOfService' as Path<T>}
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <div className="flex gap-2 items-center">
                  <FormControl>
                    <Checkbox
                      checked={field.value as boolean}
                      onCheckedChange={(e) => {
                        field.onChange(e);
                        form.trigger('termsOfService' as Path<T>);
                      }}
                    />
                  </FormControl>
                  <FormLabel className="text-xs text-[#888888]">
                    Hiermit stimme ich den{' '}
                    <Link to="https://www.7i7.de/agb" target="_blank" className="underline">
                      AGB
                    </Link>{' '}
                    und den{' '}
                    <Link to="https://www.7i7.de/datenschutz" target="_blank" className="underline">
                      Datenschutzbestimmungen
                    </Link>{' '}
                    zu. *
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Early Opt-Out Checkbox - only show when code is filled and valid
              {form.watch("code" as Path<T>) &&
                form.getValues("code" as Path<T>) !== "" &&
                form.watch("codeValid" as Path<T>) === true && form.watch("codeType" as Path<T>) === SelfCheckoutCodeType.EARLY_OPT_OUT && (
                  <FormField
                    control={form.control}
                    name={"earlyOptOutCheckbox" as Path<T>}
                    render={({ field }) => (
                      <FormItem className="flex flex-col bg-white rounded-lg">
                        <div className="flex gap-2 items-center">
                          <FormControl>
                            <Checkbox
                              checked={field.value as boolean}
                              onCheckedChange={(e) => {
                                field.onChange(e);
                                form.trigger("earlyOptOutCheckbox" as Path<T>);
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-xs text-[#888888]">
                            Wir bestätigen die Buchung des "Opt-Out-Moduls" für 150 €. Damit kann bis zu 10 Tage vor
                            Ende des ersten Monats per Kündigung an buchhaltung@7i7.de aus dem Vertrag aussteigen. *
                          </FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}*/}
          {!useInvoice && (
            <FormField
              control={form.control}
              name={'paymentConfirmation' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <div className="flex gap-2 items-center">
                    <FormControl>
                      <Checkbox
                        checked={field.value as boolean}
                        onCheckedChange={(e) => {
                          field.onChange(e);
                          form.trigger('paymentConfirmation' as Path<T>);
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-xs text-[#888888]">
                      Ich ermächtige 7i7®, Zahlungen von dem Konto mittels Lastschrift einzuziehen. *
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name={'minimumDuration' as Path<T>}
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <div className="flex gap-2 items-center">
                  <FormControl>
                    <Checkbox
                      checked={field.value as boolean}
                      onCheckedChange={(e) => {
                        field.onChange(e);
                        form.trigger('minimumDuration' as Path<T>);
                      }}
                    />
                  </FormControl>
                  <FormLabel className="text-xs text-[#888888]">
                    Die Laufzeit beträgt{' '}
                    <span className="">
                      {packages[selectedTier]?.[selectedBillingFrequency]?.minimumContractDurationMonths} Monat
                      {packages[selectedTier]?.[selectedBillingFrequency]?.minimumContractDurationMonths === 1 ? ' ' : 'e '}
                    </span>
                    und verlängert sich automatisch. <br />
                    {selectedBillingFrequency === BillingFrequency.MONTHLY ? (
                      <>
                        Die Kündigungsfrist beträgt <span className="">10 Kalendertage zum Laufzeitende im ersten Monat</span> <br />
                        und 30 Kalendertage zum Laufzeitende ab dem zweiten Monat.
                      </>
                    ) : (
                      <>
                        Die Kündigungsfrist beträgt <span className="">30 Kalendertage zum Laufzeitende</span>.
                      </>
                    )}{' '}
                    *
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Trial-Hinweis: Zeigen wenn Code gültig, Typ TRIAL und Startdatum gewählt */}
        {form.watch('code' as Path<T>) &&
          form.getValues('code' as Path<T>) !== '' &&
          form.watch('codeValid' as Path<T>) === true &&
          form.watch('codeType' as Path<T>) === SelfCheckoutCodeType.TRIAL &&
          (form.watch('newCustomerDate' as Path<T>) as string) && (
            <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600 mt-0.5" />
              <div className="text-sm text-red-700">
                <span className="font-medium">Wichtiger Hinweis:</span>
                <br />
                {(() => {
                  const startDate = form.watch('newCustomerDate' as Path<T>) as string;
                  const isAprilFirst2026 = startDate?.includes('2026-04-01');

                  if (isAprilFirst2026) {
                    return (
                      <>
                        Ab 23.03.2026 startet deine 7-tägige Testphase. Anschließend wir dein gewähltes Paket zum Startdatum aktiv. Bei Kündigung
                        endet die Testphase vorzeitig.
                      </>
                    );
                  }

                  return (
                    <>
                      Ab {formatDate(new Date(new Date(startDate).getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])} startet deine
                      7-tägige Testphase. Anschließend wird dein gewähltes Paket aktiv. Bei Kündigung endet die Testphase vorzeitig.
                    </>
                  );
                })()}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default PaymentFormItems;
